import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import axios from "axios";
import Api from "../Api/Api.json";
import { formatDate } from "../utility/DataTransformUtils";
import { BleClient, numberToUUID } from "@capacitor-community/bluetooth-le";
import { usePatientWellnessDataForDemo } from "../hooks/usePatientWellnessDataForDemo";
import WellnessCharts from "../components/WellnessCharts";
import MainContainer from "../components/Container/MainContainer";
import {
  syncDeviceData,
  subscribeSyncStatus,
  isDeviceSyncAvailable,
} from "../services/deviceSyncService";

// Conditionally import JStyle only on native platforms
let JStyle = null;
if (Capacitor.isNativePlatform()) {
  const JStyleModule = require("../JStylePlugin");
  JStyle = JStyleModule.default;
}

const WellnessPage = () => {
  const [isConnected, setConnected] = useState(false);
  const [batteryLevel, setBatteryLevel] = useState("NC");
  const [deviceTime, setStateDeviceTime] = useState("NC");
  const [devices, setDevices] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [status, setStatus] = useState("");
  const [pairState, setPairState] = useState("Pair");
  const [hrvList, setHRVList] = useState([{}]); //the hrv list returned from server
  const [sleepDurationList, setSleepDurationList] = useState([{}]);
  const [sleepStartList, setSleepStartList] = useState([{}]);
  const [sleepEndList, setSleepEndList] = useState([{}]);
  const [sleepScoreList, setSleepScoreList] = useState([{}]);
  const [dayNameList, setDayNameList] = useState([]);
  const [lastServerHRVTS, setLastServerHRVTS] = useState("");
  const [lastServerSleepTS, setLastServerSleepTS] = useState("");
  const [syncStatus, setSyncStatus] = useState("Sync not started.");
  const [deviceName, setDeviceName] = useState("energyBand");
  const [bluetoothState, setBluetoothState] = useState("on");
  // New state variables for activity data
  const [stepCountList, setStepCountList] = useState([{}]);
  const [caloriesList, setCaloriesList] = useState([{}]);
  const [distanceList, setDistanceList] = useState([{}]);
  const [userInfo, setUserInfo] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  const [deleteAccountStatus, setDeleteAccountStatus] = useState(null);
  const navigate = useNavigate();

  let userid = JSON.parse(localStorage.getItem("user_info")).di_id;
  // console.log(userid);



  useEffect(() => {
    const storedUserInfo = localStorage.getItem("user_info");
    if (storedUserInfo) {
      setUserInfo(JSON.parse(storedUserInfo));
    }
  }, []);

  useEffect(() => {
    return subscribeSyncStatus((message) => setSyncStatus(message));
  }, []);

  useEffect(() => {
    // Only initialize JStyle functionality on native platforms
    if (!Capacitor.isNativePlatform() || !JStyle) {
      setStatus("Web platform - device features not available");
      return;
    }

    JStyle.addListener("connectionResult", handleConnectionEvent); //auto connect event
    JStyle.addListener("disconnectionResult", handleDisconnectionEvent); //auto disconnect event

    JStyle.getBluetoothState().then((result) => {
      setBluetoothState(result.state);
      if (result.state === "off") {
        //show dialogue to enable bluetooth
        alert("Bluetooth is disabled. Please enable it to use the device.");
      } 
      console.log(result.state);
    });

    var jstyle_address = localStorage.getItem("jstyle_address");
    if (jstyle_address === null) {
      setPairState("Pair");
    } else {
      setPairState("Unpair");

      JStyle.isConnected().then((result) => {
        if (result.connected === true) {
          setConnected(true);
        } else {
          JStyle.connect({ deviceId: jstyle_address });
        }
      });
    }

    JStyle.checkPermission().then((result) => {
      console.log(result.permission);
      if (result.permission === "granted") {
      } else {
        alert("App Requires Bluetooth Permission.");
        JStyle.requestPermission();
      }
    });

    return () => {
      if (JStyle) {
        JStyle.removeListener("deviceFound");
        JStyle.removeListener("connectionResult");
        JStyle.removeListener("disconnectionResult");
        JStyle.removeListener("hrvDataResult");
        JStyle.stopScan();
      }
    };
  }, []);

  const pairDevice = async () => {
    if (!Capacitor.isNativePlatform() || !JStyle) {
      setStatus("Device pairing not available on web platform");
      return;
    }

    if (pairState === "Pair") {
      console.log("pairing");
      try {
        await BleClient.initialize({ androidNeverForLocation: true });

        await BleClient.requestLEScan({}, (result) => {
          console.log("received new scan result", result);
        });

        setTimeout(async () => {
          await BleClient.stopLEScan();
          console.log("stopped scanning");
        }, 5000);
      } catch (error) {
        console.log(error);
      }
      console.log("After BleClient scan");

      JStyle.isScanning().then((result) => {
        setStatus(JSON.stringify(result.scanning));
        if (!result.scanning === true) {
          setStatus("Calling start scan");
          JStyle.addListener("deviceFound", handleDiscoverDevicesEvent);
          JStyle.startScan();
        }
      });
    } else if (pairState === "Unpair") {
      console.log("unpairing");
      JStyle.disconnect();
      localStorage.removeItem("jstyle_address");
      setPairState("Pair");
    }
  };

  const forgetDevice = () => {
    localStorage.removeItem("jstyle_address");
  };

  const connectDevice = (address) => {
    if (!Capacitor.isNativePlatform() || !JStyle) {
      setStatus("Device connection not available on web platform");
      return;
    }
    JStyle.connect({ deviceId: address });
    setDevices([]);
  };

  const setDeviceTime = () => {
    if (!Capacitor.isNativePlatform() || !JStyle) {
      setStatus("Device time setting not available on web platform");
      return;
    }
    const now = new Date();
    const formattedTime = formatDate(now);
    JStyle.setDeviceTime({ date: formattedTime }); //"yyyy.MM.dd HH:mm:ss"
  };

  const getDeviceTime = () => {
    if (!Capacitor.isNativePlatform() || !JStyle) {
      setStatus("Device time not available on web platform");
      return;
    }
    JStyle.getDeviceTime().then((result) => {
      setStateDeviceTime(result.devicetime);
    });
  };

  const getBatteryLevel = () => {
    if (!Capacitor.isNativePlatform() || !JStyle) {
      setStatus("Battery level not available on web platform");
      return;
    }
    JStyle.getBatteryLevel().then((result) => {
      setBatteryLevel(result.batterylevel);
    });
  };

  const fetchDataAsync = async () => {
    if (isConnected === false) return;
    setSyncStatus("Sync started");
    console.log("Reading local storage");
    let last_sleep_ts = await localStorage.getItem("last_sleep_datetime");
    let last_hrv_ts = await localStorage.getItem("last_hrv_datetime");
    if (typeof last_sleep_ts === "undefined") {
      last_sleep_ts = "";
    }
    if (last_hrv_ts === null || last_hrv_ts === undefined) {
      console.log("assigning empty str to storage var");
      last_hrv_ts = "";
    }
    console.log(last_hrv_ts);
    JStyle.addListener("hrvDataResult", handleHRVData);
    JStyle.getHRVData({ date: last_hrv_ts }).then((result) => {
      console.log("Reading HRV data");
      if (result.hrvlist) {
        console.log(typeof result.hrvlist);
        console.log(JSON.stringify(result.hrvlist[result.hrvlist.length - 1]));
      }
    });
  };

  const handleManualSync = () => {
    if (!isDeviceSyncAvailable()) {
      setSyncStatus("Data sync not available on web platform");
      return;
    }
    if (!isConnected) {
      setSyncStatus("Sync skipped: device not connected");
      return;
    }
    syncDeviceData({ onStatus: setSyncStatus });
  };

  const setAutoMonitor = () => {
    if (!Capacitor.isNativePlatform() || !JStyle) {
      setStatus("Auto monitoring not available on web platform");
      return;
    }
    JStyle.setAutomaticMonitoring({"intervalTime":"30","datatype":"HRV","startTime":"00:00","endTime":"23:59","days":"1-1-1-1-1-1-1"}).then((result) => {
      console.log(result.setautomaticmonitoring);
    });
  };

  const clearDeviceData = () => {};

  const checkBTPermission = () => {
    if (!Capacitor.isNativePlatform() || !JStyle) {
      setStatus("Bluetooth permission check not available on web platform");
      return;
    }
    JStyle.checkPermission().then((result) => {
      console.log(result.permission);
    });
  };

  const requestBTPermission = () => {
    if (!Capacitor.isNativePlatform() || !JStyle) {
      setStatus("Bluetooth permission request not available on web platform");
      return;
    }
    JStyle.requestPermission().then((result) => {
      console.log(result.permission);
    });
  };

  const handleDiscoverDevicesEvent = (device) => {
    setDeviceName("energyBand" + device.name.slice(6));
    setDevices([...devices, device]);
  };

  const handleConnectionEvent = (deviceInfo) => {
    if (!Capacitor.isNativePlatform() || !JStyle) {
      return;
    }
    setStatus("connected");
    localStorage.setItem("jstyle_address", deviceInfo.address);
    setConnected(true);
    setDeviceTime();
    getBatteryLevel();
    setPairState("Unpair");
  };

  const handleDisconnectionEvent = (deviceInfo) => {
    setStatus("disconnected");
    setConnected(false);
  };

  const handleHRVData = (hrvResult) => {
    console.log("from listener");
    console.log(JSON.stringify(hrvResult.hrvlist));
    if (Array.isArray(hrvResult.hrvlist)) {
      console.log("array");
    }
    console.log(hrvResult.lasthrvtime);
  };

  const handleLogout = () => {
    localStorage.removeItem("user_info");
    localStorage.removeItem("jstyle_address");
    navigate("/");
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmationText.toLowerCase() !== "confirm") {
      alert("Please type 'confirm' to delete your account.");
      return;
    }

    try {
      setIsDeleteDialogOpen(false);
      setDeleteConfirmationText("");

      const headers = { headers: { "Content-Type": "application/json" } };
      const response = await axios.post(Api.FITNESS_API, {
        task: "delete_energystrap_account",
        uid: userid
      }, headers);

      if (response.data === "deleted") {
        setDeleteAccountStatus("deleted");
      } else {
        setDeleteAccountStatus("error");
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      setDeleteAccountStatus("error");
    }
  };


  return (
    <div className="text-white min-h-screen p-4 pb-20 safe-area-top">
      {/* Black background */}
      <div className="absolute inset-0 bg-black"></div>
      
      <div className="relative z-10">
        {/* Header Section */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white">Device & Profile</h1>
        </div>

      {/* Platform Warning for Web */}
      {!Capacitor.isNativePlatform() && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-yellow-800 font-medium">Web Platform</span>
          </div>
          <p className="text-yellow-700 text-sm mt-1">
            Device features are not available on web platform. Use the mobile app for full functionality.
          </p>
        </div>
      )}

      {/* Device Status Card */}
      <div className="bg-[#161B22] rounded-lg p-4 mb-6 shadow border border-[#30363D]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-white">Device Status</h2>
          <div
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              isConnected
                ? "bg-green-100 text-green-600"
                : "bg-red-100 text-red-600"
            }`}
          >
            {isConnected ? "Connected" : "Disconnected"}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-[#161B22] p-3 rounded-lg border border-[#30363D]">
            <p className="text-white text-sm">Device Name</p>
            <p className="font-medium text-white">{deviceName}</p>
          </div>
          <div className="bg-[#161B22] p-3 rounded-lg border border-[#30363D]">
            <p className="text-white text-sm">Battery Level</p>
            <p className="font-medium text-white flex items-center">
              {batteryLevel !== "NC" ? (
                <>
                  {batteryLevel}%
                  <svg
                    className="w-4 h-4 ml-2 text-green-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                    <path d="M18 9h2v2h-2V9z" />
                  </svg>
                </>
              ) : (
                "Not Available"
              )}
            </p>
          </div>
        </div>

        <div className="bg-[#161B22] p-3 rounded-lg border border-[#30363D] mb-4">
          <p className="text-white text-sm">Sync Status</p>
          <p className="font-medium text-white">{syncStatus}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={pairDevice}
            className={`px-4 py-2 rounded-lg transition-colors duration-200 flex-1 text-white ${
              pairState === "Pair"
                ? "bg-blue-500 hover:bg-blue-600"
                : "bg-red-500 hover:bg-red-600"
            }`}
          >
            {pairState}
          </button>
          <button
            onClick={forgetDevice}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200 flex-1"
          >
            Forget Device
          </button>
          <button
            onClick={handleManualSync}
            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors duration-200 flex-1"
          >
            Sync Data
          </button>
        </div>
      </div>

      {/* Device List */}
      {!isConnected && devices.length > 0 && (
        <div className="bg-[#161B22] rounded-lg p-4 mb-6 shadow border border-[#30363D]">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">
            Available Devices
          </h2>
          <div className="space-y-2">
            {devices.map((device) => (
              <div
                key={device.name}
                className="bg-white p-3 rounded-lg border border-gray-200 flex justify-between items-center"
              >
                <span className="text-white">
                  {"semWear" + device.name.slice(6)}
                </span>
                <button
                  onClick={() => connectDevice(device.mac)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg transition-colors duration-200 text-sm"
                >
                  Connect
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Profile Card */}
      {userInfo && (
        <div className="bg-[#161B22] rounded-lg p-4 mb-6 shadow border border-[#30363D]">
          <h2 className="text-xl font-semibold mb-4 text-white">Profile</h2>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-[#161B22] p-3 rounded-lg border border-[#30363D]">
              <p className="text-white text-sm">Name</p>
              <p className="font-medium text-white">{userInfo.name || "Not Available"}</p>
            </div>
            <div className="bg-[#161B22] p-3 rounded-lg border border-[#30363D]">
              <p className="text-white text-sm">Age</p>
              <p className="font-medium text-white">{userInfo.age || "Not Available"}</p>
            </div>
            <div className="bg-[#161B22] p-3 rounded-lg border border-[#30363D]">
              <p className="text-white text-sm">Gender</p>
              <p className="font-medium text-white">
                {userInfo.gender === 'M' ? 'Male' : userInfo.gender === 'F' ? 'Female' : userInfo.gender || "Not Available"}
              </p>
            </div>
            <div className="bg-[#161B22] p-3 rounded-lg border border-[#30363D]">
              <p className="text-white text-sm">Height</p>
              <p className="font-medium text-white">{userInfo.height || "Not Available"}</p>
            </div>
            <div className="bg-[#161B22] p-3 rounded-lg border border-[#30363D]">
              <p className="text-white text-sm">Weight</p>
              <p className="font-medium text-white">{userInfo.weight || "Not Available"}</p>
            </div>
          </div>
        </div>
      )}

      {/* Set Auto Monitoring Button */}
      <div className="flex justify-center mb-4">
        <button
          onClick={setAutoMonitor}
          className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200 font-medium"
        >
          Set Auto Monitoring
        </button>
      </div>

      {/* Logout Button */}
      <div className="flex justify-center mb-4">
        <button
          onClick={handleLogout}
          className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200 font-medium"
        >
          Logout
        </button>
      </div>

      {/* Delete Account Button */}
      <div className="flex justify-center">
        <button
          onClick={() => setIsDeleteDialogOpen(true)}
          className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200 font-medium"
        >
          Delete Account
        </button>
      </div>

      {/* Delete Account Confirmation Dialog */}
      {isDeleteDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#161B22] rounded-lg shadow-xl max-w-md w-full p-6 border border-[#30363D]">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-white mb-4">
                Are you sure, you want to delete your account?
              </h3>
              <p className="text-white text-sm mb-4">
                Write confirm in the text box below if you want to delete your account.
              </p>
              
              <input
                type="text"
                value={deleteConfirmationText}
                onChange={(e) => setDeleteConfirmationText(e.target.value)}
                placeholder="Type 'confirm' here"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 mb-4"
              />
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setIsDeleteDialogOpen(false);
                    setDeleteConfirmationText("");
                  }}
                  className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Status Dialog */}
      {deleteAccountStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#161B22] rounded-lg shadow-xl max-w-md w-full p-6 border border-[#30363D]">
            <div className="text-center">
              <div className="mb-4">
                <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center ${
                  deleteAccountStatus === 'deleted' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  <svg className={`w-8 h-8 ${
                    deleteAccountStatus === 'deleted' ? 'text-green-600' : 'text-red-600'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {deleteAccountStatus === 'deleted' ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    )}
                  </svg>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-white mb-4">
                {deleteAccountStatus === 'deleted' ? 'Your account is deleted' : 'Account could not be deleted. Please try again later.'}
              </h3>
              
              {deleteAccountStatus === 'deleted' ? (
                <button
                  onClick={handleLogout}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Logout
                </button>
              ) : (
                <button
                  onClick={() => setDeleteAccountStatus(null)}
                  className="w-full bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default WellnessPage;
